import React, { useState, useMemo } from 'react';
import { LabelValue } from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { SearchBoxProps } from '../type';
import { LabelValueType, mappedLabelValues } from '../LabelFacetDetails';
import { scoredSuggestions } from '../scoredSuggestions';
import { options, searchBilling, transformBilling } from './util';

type BillingCriteriaProviderContextType = {
  selected: LabelValueType[];
  setSelected: React.Dispatch<React.SetStateAction<LabelValueType[]>>;
  matchOption: (_: LabelValue[], term: string) => LabelValueType | null;
};

const matchOption = (labelId: string) => (
  _: LabelValue[],
  term: string
): LabelValueType | null => {
  if (!term) return null;

  const billingRates = searchBilling(term);

  const mappedValues = mappedLabelValues(billingRates, labelId);

  const option = mappedValues && mappedValues.length > 0 && mappedValues[0];

  return option && option.displayText ? option : null;
};

const withCriteriaProvider = (
  tag: string,
  context: React.Context<BillingCriteriaProviderContextType>,
  label: TimesheetLabel
) => (
  BaseComponent: React.FunctionComponent<SearchBoxProps>
): React.FC<SearchBoxProps> =>
  // eslint-disable-next-line react/display-name
  (props: SearchBoxProps) => {
    const { searchCriteria: { criterions = {} } = {} } = props;
    const [selected, setSelected] = useState<LabelValueType[]>(
      (criterions[tag] as LabelValueType[]) || []
    );

    const value = useMemo(
      () => ({
        selected,
        setSelected,
        matchOption: matchOption(label.id),
        suggestions: scoredSuggestions(transformBilling(options))
      }),
      [selected, setSelected]
    );

    return (
      <context.Provider value={value}>
        <BaseComponent {...props} />
      </context.Provider>
    );
  };

const makeCriteriaProvider = (label: TimesheetLabel) => (
  tag: string,
  context: React.Context<BillingCriteriaProviderContextType>
): ((
  BaseComponent: React.FunctionComponent<SearchBoxProps>
) => React.FC<SearchBoxProps>) => withCriteriaProvider(tag, context, label);

export default makeCriteriaProvider;
